/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useState } from "react";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useAppCoffeeMugContext } from "../../../Basis/Components/AppCoffeeMugContext";
import { macheEMailSicherFuerStripe } from "../../../Basis/Components/StripePaymentElement/useConfirmPayment";
import AendereZahlungsmethode from "../Commands/AendereZahlungsmethode";
import LegeZahlungsmethodeAn from "../Commands/LegeZahlungsmethodeAn";
import { translationKeys, useTranslation } from "../Internationalisierung/i18nMandanten";

type ZahlungsmethodeAendernInput = {
    fullName: string;
    eMail: string;
    land: string;
    mandantId: string,
    mandantGeaendert: string;
};

type ZahlungsmethodeAendern = (input: ZahlungsmethodeAendernInput) => Promise<void>;
type Error = string;

export default function useZahlungsmethodeAendern(): [ZahlungsmethodeAendern, Error | undefined] {
    const { t } = useTranslation();
    const [error, setError] = useState<Error | undefined>(undefined);

    const legeZahlungsmethodeAn = new LegeZahlungsmethodeAn();
    const aendereZahlungsmethode = new AendereZahlungsmethode();

    const stripe = useStripe();
    const elements = useElements();
    const appCoffeeMug = useAppCoffeeMugContext();

    const zahlungsmethodeAendern: ZahlungsmethodeAendern = async (input: ZahlungsmethodeAendernInput) => {
        if (!stripe || !elements) {
            setError(t(translationKeys.useZahlungsmethodeAendern.fehlerInitialisierung));
            return;
        }

        appCoffeeMug.show("");
        try {
            const angelegteZahlungsmethode = await legeZahlungsmethodeAn.execute({});
            if (!angelegteZahlungsmethode) {
                setError(t(translationKeys.useZahlungsmethodeAendern.fehlerZahlungsmethodeAnlegen));
                return;
            }

            const ibanElement = elements.getElement("iban");
            if (!ibanElement) {
                setError(t(translationKeys.useZahlungsmethodeAendern.fehlerInitialisierung));
                return;
            }

            const setupIntentResult = await stripe.confirmSepaDebitSetup(
                angelegteZahlungsmethode.clientGeheimnis,
                {
                    payment_method: {
                        sepa_debit: ibanElement,
                        billing_details: {
                            name: input.fullName,
                            email: macheEMailSicherFuerStripe(input.eMail),
                            address: {
                                line1: input.fullName,
                                country: input.land,
                            }
                        },
                    }
                });

            if (!setupIntentResult || setupIntentResult.error || setupIntentResult.setupIntent.id !== angelegteZahlungsmethode.zahlungsmethodeId) {
                setError(setupIntentResult?.error?.message ?? t(translationKeys.useZahlungsmethodeAendern.fehlerZahlungsdienstleister));
                return;
            }

            await aendereZahlungsmethode.execute({
                mandantId: input.mandantId,
                geaendert: input.mandantGeaendert,
                zahlungsmethodeId: angelegteZahlungsmethode.zahlungsmethodeId,
            });
        } finally {
            appCoffeeMug.hide();
        }
    }

    return [zahlungsmethodeAendern, error];
}